import * as React from "react";
import { Sparkles } from "lucide-react";
import { Button, confirmDialog, Tabs, Tooltip } from "@/components/ui";
import { HoloBorder, HoloButton, HoloShimmer } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { AspectRatio, DesignPackageItem } from "@/services/bedrock/dtos/DesignPackage";
import { isSelfActor, useViewer } from "../viewer";
import { DisabledReason, READ_ONLY_REASON } from "./DisabledReason";
import { FitFrame } from "./FitFrame";

interface CandidateCompareProps {
  item: DesignPackageItem;
  aspectRatio: AspectRatio;
  beforeUrl: string | null;
  afterUrl: string | null;
  afterState: "rendering" | "ready" | "failed";
  busy: "approving" | "discarding" | null;
  canEdit: boolean;
  isPublished: boolean;
  onKeep: () => void;
  onDiscard: () => void;
  onRetry: () => void;
  /** Why Keep and Discard are unavailable (another refinement is running). */
  disabledReason?: string | null;
}

/** Keeping a version of a published design changes what players get, so it asks first. */
export async function confirmKeep(isPublished: boolean): Promise<boolean> {
  if (!isPublished) return true;
  return confirmDialog({
    title: "Update the published design?",
    description: "Players will see the updated design in their library. Posts they've already made aren't affected.",
    confirmLabel: "Update design",
    cancelLabel: "Cancel",
  });
}

/**
 * A slide's current and new version side by side, each with its own choice under it, on master
 * previews so both sides are like for like.
 */
export const CandidateCompare: React.FC<CandidateCompareProps> = ({
  item,
  aspectRatio,
  beforeUrl,
  afterUrl,
  afterState,
  busy,
  canEdit,
  isPublished,
  onKeep,
  onDiscard,
  onRetry,
  disabledReason,
}) => {
  const [view, setView] = React.useState<"both" | "new">("both");
  const viewer = useViewer();

  const keep = async () => {
    if (await confirmKeep(isPublished)) onKeep();
  };

  const reason = !canEdit ? READ_ONLY_REASON : disabledReason ?? null;
  const locked = !!reason || busy != null;
  const request = item.candidateTemplate?.instructions;
  // A version someone else made (an admin's edit, a teammate's refinement) isn't "your change".
  const createdBy = item.candidateTemplate?.createdBy;
  const byViewer = !createdBy || isSelfActor(createdBy, viewer);
  const beforeCaption = isPublished ? "What players get today" : byViewer ? "Before your change" : "Before this change";

  const current = (
    // A muted frame the same shape and weight as the new version's holo one, so they read as a pair.
    <div
      className={cn(
        "min-w-0 flex-1 rounded-xl border-2 border-slate-200 bg-white lg:min-h-0",
        view === "new" && "hidden md:block"
      )}
    >
      <figure className="flex h-full min-w-0 flex-col gap-2 p-3 lg:min-h-0">
        <figcaption className="text-center text-xs font-bold uppercase tracking-wider text-slate-500">
          Current version
        </figcaption>
        <FitFrame aspectRatio={aspectRatio}>
          <div className="size-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
            {beforeUrl && <img src={beforeUrl} alt="Current version" className="size-full object-cover" />}
          </div>
        </FitFrame>
        <p className="text-xs text-slate-500">{beforeCaption}</p>
        <DisabledReason reason={reason} data-testid="discard-wrapper">
          <Button
            variant="outline"
            loading={busy === "discarding"}
            // The server refuses either choice while the new version is still rendering.
            disabled={locked || afterState === "rendering"}
            onClick={onDiscard}
          >
            Keep current
          </Button>
        </DisabledReason>
      </figure>
    </div>
  );

  const proposed = (
    <HoloBorder radius="xl" active={false} className="min-w-0 flex-1 lg:min-h-0">
      <figure className="flex h-full min-w-0 flex-1 flex-col gap-2 p-3 lg:min-h-0">
        <figcaption className="flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-wider text-primary">
          New version <Sparkles className="size-3" aria-hidden="true" />
        </figcaption>
        <FitFrame aspectRatio={aspectRatio}>
          {afterState === "rendering" ? (
            <HoloShimmer
              aspectRatio={aspectRatio}
              label={item.progressStatus ?? undefined}
              className="size-full rounded-xl"
            />
          ) : afterState === "failed" ? (
            <div className="flex size-full items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-destructive">
              Couldn't make this version
            </div>
          ) : (
            <div className="size-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
              {afterUrl && <img src={afterUrl} alt="New version" className="size-full object-cover" />}
            </div>
          )}
        </FitFrame>
        <p className="line-clamp-2 text-xs text-slate-500">{request ? `From “${request}”` : "Your latest change"}</p>
        {afterState === "failed" ? (
          <DisabledReason reason={reason}>
            <Button variant="outline" disabled={!!reason} onClick={onRetry}>
              Try again
            </Button>
          </DisabledReason>
        ) : (
          <DisabledReason reason={reason} data-testid="keep-wrapper">
            <HoloButton
              loading={busy === "approving"}
              disabled={locked || afterState !== "ready"}
              onClick={() => void keep()}
            >
              Use new version
            </HoloButton>
          </DisabledReason>
        )}
      </figure>
    </HoloBorder>
  );

  return (
    <div
      className="flex w-full flex-col gap-3 lg:h-full lg:min-h-0"
      data-testid="candidate-compare"
      data-item-id={item.id}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* A lighter line than the card's "Slide 1 of 3" title above it. */}
        <div className="text-sm text-slate-600">Pick the version to keep</div>
        <Tooltip>
          <Tooltip.Trigger render={<span className="text-xs text-slate-500 underline decoration-dotted" />}>
            Previews use sample data
          </Tooltip.Trigger>
          <Tooltip.Content className="max-w-64">Themes apply after you choose a version.</Tooltip.Content>
        </Tooltip>
      </div>

      <Tabs value={view} onValueChange={value => setView(value as "both" | "new")} className="md:hidden">
        <Tabs.List variant="line">
          <Tabs.Trigger value="both">Side by side</Tabs.Trigger>
          <Tabs.Trigger value="new">New only</Tabs.Trigger>
        </Tabs.List>
      </Tabs>

      <div className="flex gap-4 lg:min-h-0 lg:flex-1">
        {current}
        {proposed}
      </div>
    </div>
  );
};
